export const GOOGLE_DRIVE_FILE_SCOPE =
  'https://www.googleapis.com/auth/drive.file';

export const GOOGLE_SHEETS_MIME_TYPE =
  'application/vnd.google-apps.spreadsheet';

export const GOOGLE_DRIVE_UPLOAD_URL =
  'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink';

const createGoogleDriveError = (code, message, status) => {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
};

const errorForStatus = (status) => {
  if (status === 401 || status === 403) {
    return createGoogleDriveError(
      'google_drive_permission_denied',
      'Google Drive permission was denied or expired. Authorize access and try again.',
      status
    );
  }
  if (status === 429) {
    return createGoogleDriveError(
      'google_drive_rate_limited',
      'Google Drive is receiving too many requests. Wait a moment and try again.',
      status
    );
  }
  return createGoogleDriveError(
    'google_drive_upload_failed',
    'The template could not be saved to Google Drive. Try again.',
    status
  );
};

const createMultipartBody = ({ boundary, csvContent, fileName }) =>
  [
    `--${boundary}`,
    'Content-Type: application/json; charset=UTF-8',
    '',
    JSON.stringify({
      name: fileName,
      mimeType: GOOGLE_SHEETS_MIME_TYPE
    }),
    `--${boundary}`,
    'Content-Type: text/csv; charset=UTF-8',
    '',
    csvContent,
    `--${boundary}--`,
    ''
  ].join('\r\n');

export const createGoogleSheetFromCsv = async ({
  accessToken,
  csvContent,
  fileName
}) => {
  if (!accessToken) {
    throw createGoogleDriveError(
      'google_drive_missing_token',
      'Google Drive authorization did not return an access token.'
    );
  }

  const boundary = `proximity-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
  let response;

  try {
    response = await fetch(GOOGLE_DRIVE_UPLOAD_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': `multipart/related; boundary=${boundary}`
      },
      body: createMultipartBody({ boundary, csvContent, fileName })
    });
  } catch {
    throw createGoogleDriveError(
      'google_drive_unavailable',
      'Google Drive could not be reached. Check your connection and try again.'
    );
  }

  if (!response.ok) throw errorForStatus(response.status);

  let payload;
  try {
    payload = await response.json();
  } catch {
    throw createGoogleDriveError(
      'google_drive_invalid_response',
      'Google Drive created an unexpected response. Try again.'
    );
  }
  if (!payload?.id) {
    throw createGoogleDriveError(
      'google_drive_invalid_response',
      'Google Drive created an unexpected response. Try again.'
    );
  }

  return {
    id: payload.id,
    name: payload.name ?? fileName,
    webViewLink:
      payload.webViewLink ??
      `https://docs.google.com/spreadsheets/d/${payload.id}/edit`
  };
};
